import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { formatApiError } from "../api";
import { Input } from "../../components/ui/input";
import { Lock, Mail } from "lucide-react";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      const redirect = location.state?.from?.pathname || "/admin";
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-ink-950 flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="block mb-10 group"
          data-testid="admin-login-logo"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="relative w-12 h-12 flex items-center justify-center border border-champagne/50 group-hover:border-champagne transition-colors">
              <span className="font-display text-champagne text-2xl">V</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-2xl text-white tracking-tight">
                Maison Vellore
              </span>
              <span className="text-[10px] tracking-[0.3em] uppercase text-champagne mt-0.5">
                CMS Admin
              </span>
            </div>
          </div>
        </Link>

        <div className="bg-ink-900 border border-white/[0.06] p-10">
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
            Welkom terug
          </p>
          <h1 className="font-display text-4xl text-white mb-8 tracking-tight">
            Inloggen
          </h1>

          <form
            onSubmit={onSubmit}
            data-testid="admin-login-form"
            className="flex flex-col gap-5"
            noValidate
          >
            <label className="flex flex-col gap-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
                E-mailadres
              </span>
              <div className="relative">
                <Mail
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <Input
                  data-testid="admin-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 focus-visible:ring-offset-0 text-sm font-body text-white rounded-none"
                  autoComplete="email"
                />
              </div>
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
                Wachtwoord
              </span>
              <div className="relative">
                <Lock
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <Input
                  data-testid="admin-login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 focus-visible:ring-offset-0 text-sm font-body text-white rounded-none"
                  autoComplete="current-password"
                />
              </div>
            </label>

            {error && (
              <div
                data-testid="admin-login-error"
                className="bg-red-950/40 border border-red-900/50 px-4 py-3 text-sm text-red-300 font-body"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="mt-3 py-3.5 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold disabled:opacity-60"
            >
              {submitting ? "Inloggen..." : "Inloggen"}
            </button>
          </form>
        </div>

        <p className="mt-8 text-center text-[11px] text-zinc-600 font-body">
          Toegang alleen voor geautoriseerde administrators.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
